import React from 'react';
import type { PatientDoc } from '@usacsmideo/types';
import { useMfaRouting } from '../../hooks/useMfaRouting';
import { MfaGateFactorChoice } from './MfaGateFactorChoice';
import { MfaTermsDisclosure } from './MfaTermsDisclosure';
import { hasAcknowledgedTerms, recordTermsAcknowledgement } from '../../utils/mfa/termsDisclosureAck';

/**
 * The PHI-access gate — CU-86ajwpnek §9, ported from `mideo-health-app`'s
 * `MfaRouteGate`.
 *
 * ⚠️ ARMED AND LIVE SINCE 2026-09-01. The cutover sweep (CU-86ajnb1qh) stamps
 * `mfa.enforced: true`, so `decideMfaGate` branch 2 no longer returns "pass"
 * for a swept patient — branch 4 ("enroll") is a reachable, load-bearing path
 * for the whole population.
 *
 * ⚠️ THE RISK HAS INVERTED. While dormant, the danger here was fail-OPEN and
 * unobservable. Now it is fail-CLOSED: a bug in this component denies every
 * swept patient their own chart, all at once. Weigh changes accordingly.
 *
 * ⚠️ Branch 2 still matters and must NOT be removed — an unswept patient
 * (sweep misses, new accounts before the next run) still has `enforced` unset
 * and must still pass. "Inert" described the POPULATION, never the code.
 *
 * Do not flip enforcement from the client; that is the sweep's job.
 *
 * ⚠️ MOUNTED SO THAT EVERY AUTHENTICATED RENDER PASSES THROUGH — interactive
 * login, a page reload restoring a session, and a deep link all go through the
 * same layout. A login-handler-only gate would miss the last two.
 *
 * ⚠️ It renders the SAME enrollment sections the profile page uses, inline,
 * rather than routing away. One enrollment implementation, two hosts — a second
 * copy would drift on a security surface. The mobile app does the same.
 */

export interface MfaRouteGateProps {
  patientDoc: PatientDoc | undefined;
  children: React.ReactNode;
  /**
   * ⚠️ THE NEVER-WEDGED ESCAPE. A patient must always be able to leave a screen
   * they cannot satisfy — e.g. no passkey support AND no usable phone. Passed
   * through to every sub-view of the choice.
   */
  onSignOut?: () => void;
}

export function MfaRouteGate({ patientDoc, children, onSignOut }: MfaRouteGateProps) {
  const { status, refresh } = useMfaRouting(patientDoc);

  // ⚠️ THE PRE-GATE TERMS DISCLOSURE — CU-86ak6ez40, Phase D.
  //
  // Initialised FROM sessionStorage so a refresh mid-enrolment does not
  // re-present the terms, then held in React state for the life of the mount.
  // The state copy is what keeps a private-browsing patient (where every
  // storage access throws) from being stranded on this screen forever.
  //
  // ⚠️ FAILS OPEN TO SHOWING THE DISCLOSURE, never to skipping it — a thrown
  // read returns false. Showing the terms twice is a papercut; skipping them is
  // the compliance failure the screen exists to prevent.
  const uid = patientDoc?.uid;
  const [termsAcknowledged, setTermsAcknowledged] = React.useState(() => hasAcknowledgedTerms(uid));

  // ⚠️ A LAZY INITIALISER RUNS ONCE, ON THE FIRST MOUNT ONLY. The uid is not
  // known on that first render for a patient whose doc is still resolving, so
  // without this the acknowledgement would be missed on a refresh and the terms
  // would re-present. Re-reads only while NOT yet acknowledged, so it can never
  // walk an acknowledgement backwards.
  React.useEffect(() => {
    if (!termsAcknowledged && hasAcknowledgedTerms(uid)) setTermsAcknowledged(true);
  }, [uid, termsAcknowledged]);

  const acknowledgeTerms = React.useCallback(() => {
    recordTermsAcknowledgement(uid);
    setTermsAcknowledged(true);
  }, [uid]);

  // ⚠️ FAIL-CLOSED. Children are NOT rendered while the decision is pending. A
  // gate that shows PHI for one frame before deciding is not a gate.
  if (status === 'resolving') {
    return (
      <div data-testid="mfa-gate-resolving" className="flex h-screen flex-col items-center justify-center gap-3">
        <p>Checking your sign-in…</p>
      </div>
    );
  }

  if (status === 'enroll') {
    return (
      <div data-testid="mfa-gate-enroll" className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="w-full max-w-lg">
          {/* ⚠️ A FACTOR CHOICE, NOT A SINGLE SMS PATH (CU-86ajzr9fp). This
              previously rendered `<SmsEnrollmentSection variant="gate">` alone,
              with a comment saying `onCancel` was deliberately omitted because
              there was nowhere to cancel TO. There is now: the choice itself,
              which every sub-view returns to.

              ⚠️ THE FIRST OFFER IS "USE THE PASSKEY YOU ALREADY HAVE", not
              "enroll one" — a passkey does not mint aal2 on a PASSWORD login, so
              a passkey-enrolled patient arrives here at AAL1 holding a working
              credential (CU-86ajw3hv9). See `MfaGateFactorChoice`. */}
          {/* ⚠️ THE DISCLOSURE RENDERS ONLY INSIDE THIS BRANCH — CU-86ak6ez40.
              ⚠️ THIS IS NO LONGER A NO-OP. The D6 reasoning below described the
              PRE-CUTOVER world and was true until 2026-09-01; enforcement is
              now live, so `status` DOES reach 'enroll' and this subtree mounts
              for real patients. Treat it as a shipped surface, not a dormant
              one.

              (Historical: pre-cutover `mfa.enforced` was unset, `decideMfaGate`
              branch 2 returned "pass" for every patient, and this never
              mounted.) An already-enrolled patient is aal2, takes branch 1, and
              still never sees it.

              ⚠️ IN FRONT OF THE FACTOR CHOICE, NOT A NEW ROUTE. The gate renders
              this INSTEAD OF its children, so no child route mounts and a
              route-based screen would be unreachable. */}
          {!termsAcknowledged ? (
            <MfaTermsDisclosure onAcknowledge={acknowledgeTerms} onSignOut={onSignOut} />
          ) : (
            <MfaGateFactorChoice onComplete={refresh} onSignOut={onSignOut} />
          )}
        </div>
      </div>
    );
  }

  if (status === 'exception-bypass') {
    return (
      <>
        {/* ⚠️ NO EXPIRY COUNTDOWN. A countdown invites treating the bypass as
            an entitlement to be spent, rather than a temporary exception that
            should prompt enrollment. */}
        <div data-testid="mfa-exception-banner" className="bg-amber-100 px-4 py-2 text-sm text-amber-900">
          Your account is using a temporary access exception. Please add a passkey or text-message code from your
          profile page to keep access to your health information.
        </div>
        {children}
      </>
    );
  }

  // 'pass' — an aal2 session (branch 1) or an unswept patient (branch 2).
  // ⚠️ NOT "every patient today": enforcement armed 2026-09-01.
  return <>{children}</>;
}

export default MfaRouteGate;
